import clsx from "clsx";
import Modal from "react-modal";
import { AuthContext } from "context/AuthContext";
import { useContext, useState, useEffect, useRef } from "react";
import { SvgArrowTop, SvgClose } from "ui/svgs";
import { ActRequestType, ResDeviceType } from "webrtc-player/common/base";
import { SignalContext } from "context/SignalContext";
import { v4 as uuidv4 } from "uuid";
import { showToast } from "ui/components/common";
import DownloadItem from "ui/downloads/download-item";
import { ConfirmModalStyles } from "lib/constants";
import PubSub from "pubsub-js";
import _ from "lodash";
import { DownloadTaskType, WsResDataType } from "lib/types";
import {
  getLocalStroageByKeyId,
  appenToLocalStroageByKeyId,
  getTokenByActionAndResourceId,
} from "lib/utils";
import { DownloaderWebrtcAction } from "webrtc-player/common/downloader";
import usePlayBackRtcService from "pages/dashboard/playback/service";
import { SinglePcManagerContext } from "context/SinglePcManagerContext";

export default function DownloadingDocking() {
  const { sendDeleteDownloadRequest } = usePlayBackRtcService();
  const { user } = useContext(AuthContext);
  const { getController } = useContext(SinglePcManagerContext);
  const [active, setActive] = useState(true);
  const [worker, setWorker] = useState(null);
  const [wsDeleteRequestId] = useState("");
  const [downloaderId, setDownloaderId] = useState("");
  const [downloadList, setDownloadList] = useState<DownloadTaskType[]>([]);
  const [pollingSet, setPollingSet] = useState<Set<string>>(new Set<string>());
  const [height, setHeight] = useState("0px");
  const [clearAllModalOpen, setClearAllModalOpen] = useState(false);
  const refDownloadList = useRef<HTMLDivElement>(null);
  const refClearALL = useRef(false);
  //@ts-ignore
  const readOnly = window.__downloadReadOnly;
  //使用index或recordId设置数据
  function setRecordToDownloadList(index, changeObject, recordId?) {
    setDownloadList((oldDownloadList) => {
      if (index === null) {
        index = oldDownloadList.findIndex((item) => item.recordId === recordId);
      }
      const newDownloadedList = Array.from(oldDownloadList || []);
      if (!newDownloadedList[index]) {
        return oldDownloadList;
      }
      newDownloadedList[index] = Object.assign(
        newDownloadedList[index],
        changeObject,
      );
      appenToLocalStroageByKeyId(
        "downloadedRecords",
        user.id,
        newDownloadedList,
      );
      return newDownloadedList;
    });
  }
  function addDownloadTask(_, tasks: DownloadTaskType[]) {
    const originalDownloadList: DownloadTaskType[] =
      getLocalStroageByKeyId("downloadedRecords", user.id) || [];
    tasks = tasks.filter((item) => {
      return !originalDownloadList.find(
        (originItem) => originItem.recordId === item.recordId,
      );
    });
    const newDownloadedList = Array.from(originalDownloadList).concat(tasks);
    appenToLocalStroageByKeyId("downloadedRecords", user.id, newDownloadedList);
    setDownloadList(newDownloadedList);
  }
  function startNextDownload() {
    const nextDownloadingIndex = downloadList?.findIndex(
      (e) => e.state === "generated",
    );
    if (nextDownloadingIndex !== -1) {
      setRecordToDownloadList(nextDownloadingIndex, { state: "downloading" });
    }
  }
  async function startStatePolling(record, downloaderId) {
    const controller = await getController(
      +record.nvrId,
      record.devId,
      record.orgId,
    );

    setPollingSet((pollSet) => pollSet.add(record.recordId));
    const intervalId = setInterval(async () => {
      //get state throgh rtc,clear interval if out of generate
      const [{ token }] = await getTokenByActionAndResourceId(
        [DownloaderWebrtcAction.List],
        +record.nvrId,
        user,
        ResDeviceType.DOWNLOADER,
      );
      controller.downloaderList(
        {
          token,
          downloaderId,
          actorId: user.id.toString(),
        },
        (err, data) => {
          if (err) {
            return;
          }
          const records = data?.records || [];
          const nvrRecord = records.find(
            (item) => item.recordId === record.recordId,
          );
          if (nvrRecord && nvrRecord.state === "generated") {
            setRecordToDownloadList(
              null,
              {
                state: "generated",
                fileId: nvrRecord.contents?.[0]?.fileId,
                size: nvrRecord.contents?.[0]?.size,
              },
              record.recordId,
            );
            clearInterval(intervalId);
          }
          if (nvrRecord && nvrRecord.state === "failed") {
            setRecordToDownloadList(null, { state: "failed" }, record.recordId);
            clearInterval(intervalId);
          }
        },
      );
    }, 3000);
  }
  //init download tasks from localstorage
  useEffect(() => {
    const subscription = PubSub.subscribe("addDownloadTask", addDownloadTask);
    const localDownloadId = localStorage.getItem("downloaderId");
    const downloaderId = localDownloadId || uuidv4();
    const downloadedRecords =
      getLocalStroageByKeyId("downloadedRecords", user.id) || [];
    setDownloadList(downloadedRecords);
    localStorage.setItem("downloaderId", downloaderId);
    setDownloaderId(downloaderId);
    return () => {
      PubSub.unsubscribe("addDownloadTask", subscription);
    };
  }, []);
  // sync progress and state with localstorage every 1s
  useEffect(() => {
    //let handler;
    // if (readOnly) {
    const handler = setInterval(() => {
      const downloadedRecords =
        getLocalStroageByKeyId("downloadedRecords", user.id) || [];
      setDownloadList(downloadedRecords);
    }, 1000);
    // } else {
    //   clearInterval(handler);
    // }
    return () => {
      clearInterval(handler);
    };
  }, [readOnly]);
  // start downloading task and poll all generating tasks and make sure no repeat polls
  useEffect(() => {
    const downloadingRecord = downloadList?.find(
      (e) => e.state === "downloading",
    );
    if (!downloadingRecord) {
      startNextDownload();
    }
    // poll generating tasks,do not polling if readyonly
    if (readOnly) return;
    downloadList.forEach((item) => {
      if (item.state === "generating") {
        if (!pollingSet.has(item.recordId)) {
          startStatePolling(item, downloaderId);
        }
      }
    });
  }, [downloadList]);
  // for minimized window
  useEffect(() => {
    setHeight(
      !active
        ? "0px"
        : `${(Math.min(refDownloadList.current.scrollHeight), 366)}px`,
    );
  }, [active]);

  // create woker
  useEffect(() => {
    const worker = new Worker(new URL("./download-worker.ts", import.meta.url));
    setWorker(worker);
    worker.onmessage = function (event) {
      switch (event?.data?.action) {
        case "loaded": {
          worker.postMessage({
            action: "initDb",
            iniData: { dbName: "downloads_" + user.id },
          });

          break;
        }
        case "merged": {
          const blob = event.data.message.blob;
          PubSub.publish("onDownloadMerged", {
            recordId: event.data.message.recordId,
            fileId: event.data.message.fileId,
          });
          const downloadedRecords =
            getLocalStroageByKeyId("downloadedRecords", user.id) || [];
          const info = downloadedRecords.find(
            (e) =>
              e.recordId === event.data.message.recordId &&
              e.fileId === event.data.message.fileId,
          );
          if (!info) {
            showToast(
              1,
              "There was a problem downloading the file. Please try again.",
            );
            return;
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = info.record.name + ".mp4";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          break;
        }
        case "chunkDataAdded":
          PubSub.publish("onChunkDataAdded", {
            recordId: event.data.message.recordId,
            fileId: event.data.message.fileId,
            chunk: event.data.message.chunk,
          });
          break;
        case "downloadError":
          console.error("Worker reported an error:", event.data);
          if (event.data?.data?.error === "Exceed Quota") {
            showToast(
              1,
              "The Chrome storage space is insufficient, please check the remaining space",
            );
          }
          setRecordToDownloadList(
            null,
            {
              state: "failed",
              fileId: event.data?.data?.fileId,
              recordId: event?.data.data?.recordId,
            },
            event?.data.data?.recordId,
          );
          break;
        case "mergedError":
          console.error("Worker reported an error:", event.data);
          break;
      }
    };
  }, []);

  const clearLocalData = () => {
    worker.postMessage({ action: "clearAll" });
    appenToLocalStroageByKeyId("downloadedRecords", user.id, []);
    appenToLocalStroageByKeyId("downloadingRecords", user.id, []);
    setDownloadList([]);
  };
  const deleteAllDownloads = async () => {
    const requests = [];
    downloadList?.forEach((download) => {
      requests.push({
        actorId: user.id.toString(),
        downloaderId: downloaderId,
        recordId: download.recordId,
        camera: {
          id: download.camera.id,
          macAddress: download.camera.macAddress,
          remoteIndex: download.camera.remoteIndex,
        },
      });
    });
    try {
      const res = await sendDeleteDownloadRequest(requests, user.orgId);
      const data = (res as WsResDataType)?.deviceDownloaderDeleteReply?.result;
      const errorData = data?.find((e) => e?.error);
      if (errorData) {
        showToast(
          1,
          "There was a problem remove some of the downloads. Please try again.. Error:" +
            errorData?.error?.message,
        );
        return;
      }
      showToast(0, "All downloads have been removed.", { minWidth: 300 });
      clearLocalData();
      refClearALL.current = true;
      setClearAllModalOpen(false);
    } catch (error) {
      console.error("Error deleting downloads:", error);
      showToast(
        1,
        "There was a problem remove all of the downloads. Please try again. Error:" +
          error?.message,
      );
    }
  };
  const rotate = !active ? "rotateX(0deg)" : "rotateX(180deg)";
  return (
    <div
      className={clsx(
        "w-[366px] shadow-[-10px_10px_40px_10px_rgba(0,0,0,0.2)] rounded-t-xl bg-white overflow-hidden",
        downloadList.length === 0 ? "hidden" : "",
      )}
    >
      <div className="flex justify-between items-center px-6 py-4 font-semibold bg-[#F6F8FC]">
        Downloading
        <div>
          <button
            onClick={() => {
              setActive(!active);
            }}
            className="mr-4"
            style={{ transform: rotate, transition: "all 0.2s linear" }}
          >
            <SvgArrowTop width="13" className="fill-neutral-500 mr-1" />
          </button>
          {!readOnly && (
            <button
              onClick={() => {
                setClearAllModalOpen(true);
              }}
            >
              <SvgClose width="14" className="fill-neutral-500" />
            </button>
          )}
        </div>
      </div>
      <div
        ref={refDownloadList}
        className="overflow-y-auto"
        style={{ maxHeight: `${height}`, transition: "max-height 0.4s ease" }}
      >
        {downloadList?.map((item, index) => {
          return (
            <div
              className="bg-white w-full h-[68px]"
              key={item.recordId + item.fileId}
            >
              <DownloadItem
                readOnly={readOnly}
                downloadInfo={item}
                worker={worker}
                downloaderId={downloaderId}
                onOneChunkSaved={(chunk) => {
                  const i = downloadList.findIndex(
                    (e) =>
                      e.recordId === item.recordId && e.fileId === item.fileId,
                  );
                  if (i < 0) {
                    return;
                  }
                  setRecordToDownloadList(i, { chunk: chunk });
                }}
                onFailed={() => {
                  const i = downloadList.findIndex(
                    (e) =>
                      e.recordId === item.recordId && e.fileId === item.fileId,
                  );
                  if (i < 0) {
                    return;
                  }
                  setRecordToDownloadList(i, { state: "failed" });
                }}
                // start next download
                onFinished={() => {
                  const i = downloadList.findIndex(
                    (e) =>
                      e.recordId === item.recordId && e.fileId === item.fileId,
                  );
                  if (i < 0) {
                    return;
                  }
                  setRecordToDownloadList(i, { state: "downloaded" });
                  startNextDownload();
                }}
                onDeleted={() => {
                  const i = downloadList.findIndex(
                    (e) =>
                      e.recordId === item.recordId && e.fileId === item.fileId,
                  );
                  const newDownloadedList = Array.from(downloadList);
                  newDownloadedList.splice(i, 1);
                  setDownloadList(newDownloadedList);
                  appenToLocalStroageByKeyId(
                    "downloadedRecords",
                    user.id,
                    newDownloadedList,
                  );
                }}
              />
            </div>
          );
        })}
      </div>
      <Modal
        isOpen={clearAllModalOpen}
        style={ConfirmModalStyles}
        contentLabel="Example Modal"
      >
        <h1 className="text-xl font-bold">Remove Downloads?</h1>
        <div className="text-base mt-4">
          Are you sure you want to remove all of the downloads?
        </div>

        <div className="flex justify-end mt-6">
          <button
            className="rounded-2xl bg-white h-[54px] w-[230px] flex items-center justify-center border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              deleteAllDownloads();
            }}
          >
            {wsDeleteRequestId ? (
              <div
                className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Remove all of the Downloads"
            )}
          </button>
          <button
            className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setClearAllModalOpen(false);
            }}
          >
            Continue Download
          </button>
        </div>
      </Modal>
    </div>
  );
}
